import type { GestureEvent } from '../types/planet';
import { GestureEngine } from './GestureEngine';
import { Hands } from '@mediapipe/hands';

/**
 * 手势追踪器
 *
 * === 三层叠加架构（从底到顶）===
 *
 * 第 1 层：#webcam-bg     <video>   摄像头画面（z-index: 0，最底层背景）
 * 第 2 层：Three.js       <canvas>  太阳系 3D 渲染（z-index: 1，alpha:true 透明）
 * 第 3 层：#hand-overlay   <canvas>  手部骨架线（z-index: 20，在 Three.js 之上）
 *
 * UI 层：按钮、调试面板等                         （z-index: 200+）
 *
 * 效果：摄像头画面作为背景，太阳系透明叠加在上面，
 *       用户能看到自己的手在星系中的位置，手指可以"指向"星球
 */

export class HandTracker {
  private videoEl: HTMLVideoElement;
  private overlayCanvas: HTMLCanvasElement;
  private overlayCtx: CanvasRenderingContext2D;
  private gestureEngine: GestureEngine;
  private onGesture: (event: GestureEvent) => void;
  private debugEl: HTMLElement;

  private hands: Hands | null = null;
  private isRunning: boolean = false;
  private animFrameId: number | null = null;

  // 手势状态缓存，用于 overlay 绘制
  private lastLandmarks: any[] | null = null;
  private _sendErrorLogged: boolean = false;  // 防止错误日志刷屏
  private _frameCount: number = 0;           // 检测帧计数器（调试用）
  private _lastHandCount: number = 0;        // 最近一次检测到的手数

  constructor(onGesture: (event: GestureEvent) => void) {
    this.onGesture = onGesture;

    this.videoEl = this.createVideoBg();
    this.overlayCanvas = this.createOverlayCanvas();
    this.overlayCtx = this.overlayCanvas.getContext('2d')!;
    this.debugEl = this.createDebugEl();

    this.gestureEngine = new GestureEngine((event) => {
      this.onGesture(event);
    });
    this.gestureEngine.onDebugInfo = (info) => {
      this.debugEl.textContent = info;
    };

    // 窗口大小变化时同步 overlay 尺寸
    window.addEventListener('resize', () => this.resizeOverlay());
  }

  /**
   * 创建全屏半透明摄像头视频背景（第 1 层）
   * 注意：不用 display:none，用 opacity:0 隐藏，确保浏览器正常渲染视频流
   */
  private createVideoBg(): HTMLVideoElement {
    let el = document.getElementById('webcam-bg') as HTMLVideoElement;
    if (!el) {
      el = document.createElement('video');
      el.id = 'webcam-bg';
      el.autoplay = true;
      el.muted = true;
      el.playsInline = true;
      // 关键：初始 opacity=0 但 display=block，让浏览器分配渲染资源
      el.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        object-fit: cover;
        transform: scaleX(-1);
        opacity: 0;
        transition: opacity 0.8s ease;
        z-index: 0;        /* 最底层 */
        pointer-events: none;
        display: block;
      `;
      document.body.appendChild(el);
    }
    return el;
  }

  /**
   * 创建全屏手部骨架线叠加画布（第 2 层）
   */
  private createOverlayCanvas(): HTMLCanvasElement {
    let existing = document.getElementById('hand-overlay') as HTMLCanvasElement;
    if (!existing) {
      existing = document.createElement('canvas');
      existing.id = 'hand-overlay';
      existing.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 20;       /* 在 Three.js (z:1) 之上 */
        pointer-events: none;
        display: none;
      `;
      document.body.appendChild(existing);
    }
    this.resizeOverlay();
    return existing;
  }

  /** 同步 overlay canvas 尺寸与窗口一致 */
  private resizeOverlay() {
    if (this.overlayCanvas) {
      this.overlayCanvas.width = window.innerWidth;
      this.overlayCanvas.height = window.innerHeight;
    }
  }

  /** 调试信息面板 */
  private createDebugEl(): HTMLElement {
    let existing = document.getElementById('gesture-debug');
    if (!existing) {
      existing = document.createElement('div');
      existing.id = 'gesture-debug';
      existing.style.cssText = `
        position: fixed;
        top: 70px;
        right: 20px;
        background: rgba(0,0,0,0.75);
        color: #00ff88;
        padding: 12px 18px;
        border-radius: 10px;
        font-size: 13px;
        font-family: monospace;
        z-index: 200;
        max-width: 400px;
        border: 1px solid rgba(0,255,136,0.35);
        display: none;
        white-space: pre-line;
        line-height: 1.6;
        pointer-events: none;
        backdrop-filter: blur(6px);
      `;
      document.body.appendChild(existing);
    }
    return existing;
  }

  /**
   * 初始化并启动手势追踪
   */
  public async init(): Promise<void> {
    try {
      // 先让 video 可见但半透明，确保浏览器分配了渲染资源
      this.videoEl.style.display = 'block';
      this.videoEl.style.opacity = '0';

      // 重置状态
      this._sendErrorLogged = false;
      this.lastLandmarks = null;

      await this.initHands();
      await this.startNativeCamera();

      console.log('✅ 手势追踪初始化成功，视频尺寸:', this.videoEl.videoWidth, 'x', this.videoEl.videoHeight);
      this.debugEl.textContent =
        `✅ 手势追踪已启动\n` +
        `📹 视频: ${this.videoEl.videoWidth}×${this.videoEl.videoHeight}\n` +
        `请将手放在摄像头前`;

      // ★ 关键：给 body 加 gesture-active 类，CSS 会自动把 #webcam-bg opacity 设为 0.35
      document.body.classList.add('gesture-active');

      // 显示 overlay + debug 面板（video opacity 由 CSS 控制）
      requestAnimationFrame(() => {
        this.overlayCanvas!.style.display = 'block';
        this.debugEl.style.display = 'block';
        console.log('📹 激活后 video computed style:', {
          opacity: getComputedStyle(this.videoEl).opacity,
          display: getComputedStyle(this.videoEl).display,
        });
      });

      // 启动检测循环
      this.isRunning = true;
      this.detectLoop();
    } catch (err) {
      console.error('❌ 手势追踪初始化失败：', err);
      const msg = (err as Error).message || String(err);
      this.debugEl.textContent = msg.startsWith('❌') ? msg : `❌ 启动失败：${msg}\n请检查摄像头权限`;
      throw err;
    }
  }

  /** 初始化 MediaPipe Hands 模型 */
  private initHands(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.hands = new Hands({
        locateFile: (file: string) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`,
      });

      this.hands.setOptions({
        maxNumHands: 2,
        modelComplexity: 1,
        minDetectionConfidence: 0.5,   // 降低阈值，更容易检测到手
        minTrackingConfidence: 0.5,
      });

      this.hands.onResults((results: any) => {
        this.onHandResults(results);
      });

      // ★ 必须调用 initialize() 加载模型
      this.hands.initialize().then(() => {
        console.log('✅ MediaPipe Hands 模型加载完成');
        resolve();
      }).catch((err: Error) => {
        console.error('❌ MediaPipe Hands 模型加载失败:', err);
        reject(err);
      });
    });
  }

  /** 原生 getUserMedia 获取摄像头流 */
  private async startNativeCamera(): Promise<void> {
    // ★ 检查 mediaDevices 是否可用（非 HTTPS / localhost 环境会被浏览器禁用）
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const msg = '❌ 摄像头不可用\n' +
        '原因：浏览器仅在 HTTPS 或 localhost 环境允许访问摄像头\n' +
        '当前地址：' + window.location.href + '\n' +
        '解决：请通过 https:// 或 localhost 访问，\n' +
        '或者手动允许浏览器摄像头权限';
      throw new Error(msg);
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: 'user',
      },
      audio: false,
    });

    this.videoEl.srcObject = stream;

    return new Promise((resolve) => {
      this.videoEl.onloadedmetadata = () => {
        this.videoEl.play().then(resolve);
      };
    });
  }

  /** 检测循环 */
  private detectLoop() {
    if (!this.isRunning || !this.hands) return;

    if (
      this.videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      !this.videoEl.paused &&
      !this.videoEl.ended
    ) {
      this.hands.send({ image: this.videoEl }).catch((err) => {
        // 只在首次错误时打印，避免刷屏
        if (!this._sendErrorLogged) {
          console.error('❌ hands.send() 错误:', err);
          this.debugEl.textContent = `❌ 检测错误: ${err}\n请刷新重试`;
          this._sendErrorLogged = true;
        }
      });
    }

    this.animFrameId = requestAnimationFrame(() => this.detectLoop());
  }

  /** 处理检测结果 */
  private onHandResults(results: any) {
    this._frameCount++;

    // 缓存最新数据用于 overlay 绘制
    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      this.lastLandmarks = results.multiHandLandmarks;
      this.gestureEngine.analyze(results.multiHandLandmarks);

      // 更新调试信息：注意不要在这里覆盖 debugEl.textContent
      // GestureEngine.onDebugInfo 回调已经在 analyze() 中设置了实时分数信息
      // 这里只在没有手势引擎调试信息时设置兜底提示（由 onResults 末尾统一处理）
      this._lastHandCount = results.multiHandLandmarks.length;
    } else {
      this.lastLandmarks = null;
      // 每 30 帧更新一次"未检测"提示，避免频繁 DOM 操作
      if (this._frameCount % 30 === 1) {
        this.debugEl.textContent =
          `👀 未检测到手部 [#${this._frameCount}]\n` +
          `请将手放在摄像头前\n` +
          `（帧循环正常运行中）`;
      }
    }

    // 每帧都重绘 overlay
    this.drawOverlay();
  }

  /**
   * 在全屏 overlay 上绘制手部骨架线
   * 坐标直接映射到屏幕像素，这样手的位置和星系位置对应
   */
  private drawOverlay() {
    const ctx = this.overlayCtx;
    const w = this.overlayCanvas.width;
    const h = this.overlayCanvas.height;

    ctx.clearRect(0, 0, w, h);

    if (!this.lastLandmarks || this.lastLandmarks.length === 0) return;

    const HAND_CONNECTIONS = [
      [0,1],[1,2],[2,3],[3,4],         // 拇指
      [5,6],[6,7],[7,8],               // 食指
      [9,10],[10,11],[11,12],          // 中指
      [13,14],[14,15],[15,16],         // 无名指
      [17,18],[18,19],[19,20],         // 小指
      [0,5],[5,9],[9,13],[13,17],      // 掌心
    ];

    for (const landmarks of this.lastLandmarks) {
      // ---- 连接线 ----
      ctx.strokeStyle = '#00ff88';
      ctx.lineWidth = 2;
      ctx.shadowColor = '#00ff88';
      ctx.shadowBlur = 8;
      ctx.lineCap = 'round';
      for (const [i, j] of HAND_CONNECTIONS) {
        if (i >= landmarks.length || j >= landmarks.length) continue;
        const x1 = (1 - landmarks[i].x) * w;  // 镜像 X
        const y1 = landmarks[i].y * h;
        const x2 = (1 - landmarks[j].x) * w;
        const y2 = landmarks[j].y * h;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // ---- 关键点 ----
      for (let i = 0; i < landmarks.length; i++) {
        const x = (1 - landmarks[i].x) * w;
        const y = landmarks[i].y * h;
        const isIndexTip = (i === 8);  // 食指指尖

        ctx.beginPath();
        ctx.arc(x, y, isIndexTip ? 5 : 2.5, 0, Math.PI * 2);
        ctx.fillStyle = isIndexTip ? '#ff4444' : '#00ff88';
        ctx.shadowColor = isIndexTip ? '#ff4444' : '#00ff88';
        ctx.shadowBlur = isIndexTip ? 8 : 5;
        ctx.fill();

        // 食指指尖额外画一个外圈光晕（指示"点击点"）
        if (isIndexTip) {
          ctx.beginPath();
          ctx.arc(x, y, 10, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(255,68,68,0.35)';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      }
    }

    ctx.shadowBlur = 0;
  }

  /** 获取食指指尖的屏幕坐标（供外部做射线检测用） */
  public getIndexTipPosition(): { x: number; y: number } | null {
    if (!this.lastLandmarks || this.lastLandmarks[0]?.length < 9) return null;
    const lm = this.lastLandmarks[0];
    return {
      x: (1 - lm[8].x) * window.innerWidth,
      y: lm[8].y * window.innerHeight,
    };
  }

  /** 获取当前识别到的所有手的关键点 */
  public getLandmarks() { return this.lastLandmarks; }

  /**
   * 停止追踪
   */
  public stop() {
    this.isRunning = false;

    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }

    if (this.videoEl.srcObject) {
      const stream = this.videoEl.srcObject as MediaStream;
      stream.getTracks().forEach(t => t.stop());
      this.videoEl.srcObject = null;
    }

    if (this.hands) {
      this.hands.close();
      this.hands = null;
    }

    // ★ 移除 gesture-active 类，CSS 会自动把 video opacity 设回 0
    document.body.classList.remove('gesture-active');

    // 清空并隐藏 overlay
    this.overlayCtx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    this.overlayCanvas.style.display = 'none';
    this.lastLandmarks = null;

    this.debugEl.textContent = '🛑 手势追踪已停止';
  }
}
